import { findGameBySlug } from '../utils/gameSlug.js';

// Whether the game a /game/<slug>/... URL names can be opened by this user - decided from the org catalogue the
// game-access guard already loads, each game tagged `accessible` by the server.
//
// Reported: opening a game the user had no access to (by URL, a bookmark, a shared link) rendered that game's pages,
// each failing on its own with a raw "Failed to load segments - Insufficient permissions". The guard only ever
// blocked users with NO game at all.

/** The game slug in a /game/... path; null outside one, on the games list, and in the creation wizard. */
export const routeGameSlug = (pathname) => {
    const parts = String(pathname || '').split('/').filter(Boolean);
    if (parts[0] !== 'game' || !parts[1] || parts[1] === 'new') return null;
    try {
        return decodeURIComponent(parts[1]);
    } catch {
        return parts[1];
    }
};

/**
 * Whether the guard's accessibility snapshot still answers for now: loaded, for this org and selected game, and taken
 * at the current access version. A snapshot from before an access change is stale even when org and game match - it
 * is re-read in the background (the page keeps rendering from it meanwhile). Reported: a member whose suspension on
 * Solitaire was lifted while they were on Profile still got "Your access to Solitaire is suspended" opening the game,
 * because the snapshot from before was reused - the guard only hears the change while a game page is mounted.
 */
export const snapshotIsCurrent = (snapshot, { orgId, gameId, accessVersion = 0 }) => !!snapshot?.loaded
    && snapshot.orgId === orgId
    && snapshot.gameId === gameId
    && (snapshot.accessVersion || 0) === (accessVersion || 0);

/**
 * `{ state, game }`:
 *  - 'none'    - the path names no game (or the catalogue is not loaded yet): nothing to decide here;
 *  - 'ok'      - a game the user can open;
 *  - 'suspended' - a game an owner suspended the user's access to: said as such, never "request access";
 *  - 'locked'  - a game of this org the user cannot open (`game` is its catalogue record);
 *  - 'unknown' - no game of this org has that name.
 */
export const routeGameAccess = (pathname, catalogue) => {
    const slug = routeGameSlug(pathname);
    if (!slug || !Array.isArray(catalogue)) return { state: 'none', game: null };
    const game = findGameBySlug(catalogue, slug);
    if (!game) return { state: 'unknown', game: null };
    if (game.accessible) return { state: 'ok', game };
    return { state: game.suspended ? 'suspended' : 'locked', game };
};
